import React, { useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { CreditCard, ShieldCheck, Loader2 } from 'lucide-react';
import { Button } from './ui/button';
import { API_URL } from '../api/api';

/**
 * ExternalCheckoutSimulator component.
 * This simulates a generic external payment gateway (like Stripe or a custom wire transfer form).
 * It exists entirely independently of the Mailwise authenticated app.
 */
export default function ExternalCheckoutSimulator() {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();

    const sessionId = searchParams.get('session_id');
    const workspaceId = searchParams.get('workspaceId');
    const planCode = searchParams.get('planCode');
    const successUrl = searchParams.get('success_url');
    const cancelUrl = searchParams.get('cancel_url');

    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const handlePay = async () => {
        setLoading(true);
        setError(null);

        try {
            // Trigger the simulated webhook locally
            await axios.post(`${API_URL}/api/billing/mock-trigger`, {
                type: 'checkout.session.completed',
                data: {
                    object: {
                        id: sessionId,
                        client_reference_id: workspaceId,
                        customer: `cus_ext_${workspaceId.substring(0, 8)}`,
                        subscription: `sub_ext_${Date.now()}`,
                        metadata: {
                            planCode: planCode
                        }
                    }
                }
            });

            // If success, redirect to success_url
            if (successUrl) {
                window.location.href = successUrl;
            } else {
                navigate('/plan');
            }
        } catch (err) {
            console.error('Mock payment error:', err);
            setError('Payment failed to process. Try again.');
            setLoading(false);
        }
    };

    const handleCancel = () => {
        if (cancelUrl) {
            window.location.href = cancelUrl;
        } else {
            navigate('/plan');
        }
    };

    if (!sessionId) {
        return (
            <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
                <div className="bg-white p-8 rounded-xl shadow-lg w-full max-w-md text-center">
                    <h2 className="text-xl font-bold text-red-600 mb-2">Invalid Session</h2>
                    <p className="text-gray-600 mb-6">No checkout session ID provided.</p>
                    <Button onClick={() => navigate('/login')}>Return Home</Button>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4 font-sans text-gray-900">
            <div className="bg-white p-0 rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden border border-gray-100">
                {/* External Provider Header */}
                <div className="bg-slate-900 p-6 text-center">
                    <div className="flex justify-center mb-3">
                        <div className="w-12 h-12 bg-blue-600 rounded-xl flex items-center justify-center text-white shadow-lg">
                            <CreditCard className="w-6 h-6" />
                        </div>
                    </div>
                    <h2 className="text-xl font-bold text-white">External Payment Gateway</h2>
                    <p className="text-slate-400 text-sm mt-1">Simulating Custom Commerce Provider</p>
                </div>

                <div className="p-8">
                    {error && (
                        <div className="mb-6 bg-red-50 text-red-600 p-3 rounded-lg text-sm font-medium text-center">
                            {error}
                        </div>
                    )}

                    <div className="space-y-6">
                        <div className="bg-gray-50 p-4 rounded-xl border border-gray-100">
                            <div className="flex justify-between items-center mb-2">
                                <span className="text-gray-500 text-sm">Target Plan</span>
                                <span className="font-bold text-lg">{planCode} Plan</span>
                            </div>
                            <div className="flex justify-between items-center">
                                <span className="text-gray-500 text-sm">Client Reference</span>
                                <span className="font-mono text-xs text-gray-600 bg-gray-200 px-2 py-1 rounded">{workspaceId.substring(0, 8)}...</span>
                            </div>
                        </div>

                        <div>
                            <p className="text-sm text-gray-500 mb-3 text-center flex items-center justify-center gap-1">
                                <ShieldCheck className="w-4 h-4 text-green-500" />
                                This simulates an external payment gateway.
                            </p>

                            <Button
                                className="w-full h-12 text-lg bg-blue-600 hover:bg-blue-700 text-white shadow-lg shadow-blue-200 transition-all"
                                onClick={handlePay}
                                disabled={loading}
                            >
                                {loading ? (
                                    <><Loader2 className="w-5 h-5 mr-2 animate-spin" /> Processing payment...</>
                                ) : (
                                    'Simulate External Confirmation'
                                )}
                            </Button>

                            <Button
                                variant="ghost"
                                className="w-full mt-3 text-gray-500 hover:text-gray-800"
                                onClick={handleCancel}
                                disabled={loading}
                            >
                                Cancel and return
                            </Button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
